import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { Link as RouterLink } from 'react-router-dom';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    TablePagination,
    Button,
    TextField,
    MenuItem,
    CircularProgress,
    Chip,
    Tooltip,
} from '@mui/material';
import { API_URL } from '../../api';
import InvoicesTabs from '../../components/InvoicesTabs';
import HowItWorksCard from '../../components/HowItWorksCard';
import { useConfirm } from '../../components/ConfirmDialog';
import AppButton from '../../components/AppButton';
import YearGroupSelect from '../../components/YearGroupSelect';
import TermSelect from '../../components/TermSelect';
import { shortYear } from '../../constants/yearGroups';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const STATUS_COLORS = { draft: 'default', sent: 'info', paid: 'success', overdue: 'warning', void: 'default' };
const STATUS_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'outstanding', label: 'Outstanding (sent + overdue)' },
    { value: 'sent', label: 'Sent' },
    { value: 'paid', label: 'Paid' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'draft', label: 'Draft' },
    { value: 'void', label: 'Void' },
];

const fmt = (amount, currency = 'GBP') =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(amount) || 0);

const recipientLabel = (i) => {
    if (i.parentId) return `${i.parentId.firstName || ''} ${i.parentId.surname || ''}`.trim();
    if (i.recipientName) return i.recipientName;
    if (i.recipientEmail) return i.recipientEmail;
    return '—';
};

const studentSubline = (i) => {
    const acc = i.parentId;
    // Invoice-level yearGroup takes precedence — captures the form override
    // and the contact-enquiry / manual-entry fallback path.
    const invYr = shortYear(i.yearGroup);
    if (!acc) return invYr;
    if (acc.role === 'student') {
        return invYr || shortYear(acc.yearGroup);
    }
    // Parent recipient — student info lives on childInfo.
    const name = acc.childInfo?.studentName?.trim() || '';
    const yr = invYr || shortYear(acc.childInfo?.yearGroup);
    if (name && yr) return `${name} (${yr})`;
    return name || yr;
};

export default function InvoicesPage() {
    const confirm = useConfirm();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [loading, setLoading] = useState(true);

    const [status, setStatus] = useState('');
    const [yearGroup, setYearGroup] = useState('');
    const [term, setTerm] = useState('');
    const [q, setQ] = useState('');
    const [reminderBusyId, setReminderBusyId] = useState(null);

    const sendReminder = async (id) => {
        if (!(await confirm({ title: 'Send reminder', message: 'Send a payment reminder email to this recipient?', confirmLabel: 'Send' }))) return;
        setReminderBusyId(id);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/invoices/${id}/send-reminder`,
                {},
                authHeaders()
            );
            setItems((arr) =>
                arr.map((it) =>
                    it._id === id
                        ? {
                              ...it,
                              remindersSentCount: data.remindersSentCount,
                              lastReminderSentAt: data.lastReminderSentAt,
                          }
                        : it
                )
            );
        } catch (err) {
            alert(err?.response?.data?.message || 'Failed to send reminder.');
        } finally {
            setReminderBusyId(null);
        }
    };

    const buildQuery = () => {
        const params = new URLSearchParams();
        params.set('page', page + 1);
        params.set('limit', rowsPerPage);
        if (status) params.set('status', status);
        if (yearGroup) params.set('yearGroup', yearGroup);
        if (term) params.set('term', term);
        if (q.trim()) params.set('q', q.trim());
        return params;
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API_URL}/api/admin/invoices?${buildQuery().toString()}`,
                authHeaders()
            );
            setItems(data.items || []);
            setTotal(data.total || 0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [page, rowsPerPage]);

    const submit = (e) => {
        e.preventDefault();
        if (page !== 0) setPage(0);
        else load();
    };

    const downloadCsv = () => {
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        if (yearGroup) params.set('yearGroup', yearGroup);
        if (term) params.set('term', term);
        const url = `${API_URL}/api/admin/invoices/export.csv?${params.toString()}`;
        fetch(url, { headers: { Authorization: cookies.get('TOKEN') } })
            .then((res) => {
                if (!res.ok) throw new Error('CSV export failed');
                return res.blob();
            })
            .then((blob) => {
                const objUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = objUrl;
                a.download = `invoices-${status || 'all'}-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(objUrl);
            })
            .catch((err) => alert(err.message));
    };

    return (
        <Container>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1, gap: 2, flexWrap: 'wrap', rowGap: 2 }}
            >
                <Typography variant="h4">Invoices</Typography>
                {/* Branded rotation via the shared AppButton wrapper:
                      primary   = New invoice (main creation flow)
                      secondary = Schedule invoice (adjacent creation)
                      tertiary  = Export CSV (utility) */}
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }}>
                    <AppButton
                        component={RouterLink}
                        to="/invoices/new"
                        variant="primary"
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        New invoice
                    </AppButton>
                    <AppButton
                        component={RouterLink}
                        to="/invoice-schedules/new"
                        variant="secondary"
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Schedule invoice
                    </AppButton>
                    <AppButton
                        onClick={downloadCsv}
                        variant="tertiary"
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Export CSV
                    </AppButton>
                </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
                Bill parents and contact-form enquiries for tutoring. Use <strong>New invoice</strong>
                {' '}to draft a bill, <strong>Edit</strong> on a draft to revise it, <strong>PDF</strong>
                {' '}to preview without sending, <strong>Send reminder</strong> for outstanding ones,
                and <strong>Duplicate</strong> to clone an old invoice (a fresh invoice number and
                payment reference are issued). Bank Transfer Details are pulled from{' '}
                <Box component={RouterLink} to="/settings" sx={{ color: 'primary.main', textDecoration: 'none' }}>
                    Settings
                </Box>{' '}
                unless you override them on the invoice form. Drafts are editable; once an invoice has
                been sent or paid its content is frozen.
            </Typography>

            <HowItWorksCard title="Invoice statuses & lifecycle">
                <Typography variant="caption" component="div">
                    <strong>Lifecycle of an invoice:</strong><br />
                    <strong>1. Draft</strong> — you've clicked <em>New invoice</em>, filled in the
                    line items and saved. The parent <strong>doesn't see it yet</strong>. You can
                    still <em>Edit</em>, add lines, tweak amounts, or <em>Delete</em>. Nothing is
                    sent.<br />
                    <strong>2. Sent</strong> — you clicked <em>Send</em>. The final PDF is generated,
                    the parent gets an email with a payment link (Stripe) plus bank transfer
                    details, and the invoice appears on their portal dashboard. From this point the
                    content is <strong>frozen</strong> — no more editing.<br />
                    <strong>3. Overdue</strong> — set automatically by the nightly cron
                    (02:00 server time) when a <em>Sent</em> invoice's due date passes. Nothing you
                    have to do; it's just a label. Use <em>Send reminder</em> to nudge the parent.<br />
                    <strong>4. Paid</strong> — the invoice has been settled. Two ways this happens:
                    (a) Stripe webhook confirms a card payment automatically, or (b) you clicked{' '}
                    <em>Mark paid</em> after a bank transfer landed. <em>paidAt</em> is stamped.<br />
                    <strong>5. Void</strong> — a mistake / cancellation. Voided invoices don't count
                    towards outstanding totals or appear on the parent portal. A full Stripe refund
                    also flips a paid invoice to <em>Void</em> automatically.<br />
                    <strong>What the parent sees:</strong> only <em>Sent</em>, <em>Overdue</em>, and{' '}
                    <em>Paid</em> invoices appear on their portal. Drafts and voids stay admin-side.<br />
                    <strong>"Awaiting confirmation":</strong> the parent has clicked{' '}
                    <em>Mark payment as made</em> to tell you they've paid by bank transfer. This
                    doesn't change the status — you still need to verify the transfer landed and
                    then click <em>Mark paid</em> yourself. The row is flagged in the admin list
                    until you do.<br />
                    <strong>The Outstanding tab</strong> = <em>Sent</em> + <em>Overdue</em>. That's
                    everything actually waiting on payment, at a glance.<br />
                    <strong>Send reminder</strong> — only enabled on <em>Sent</em> / <em>Overdue</em>.
                    Increments a counter so you can see how many nudges have gone out.<br />
                    <strong>Duplicate</strong> — clones an existing invoice into a new{' '}
                    <em>Draft</em> with a fresh invoice number and a new payment reference (never
                    reuses the old one).<br />
                    <strong>Schedule invoice</strong> — sets up a recurring or future-dated invoice
                    (see the <em>Scheduled</em> tab). The scheduler creates a fresh draft on the
                    trigger date and — if you tick <em>send automatically</em> — will also send it
                    without your review.
                </Typography>
            </HowItWorksCard>

            <InvoicesTabs current="invoices" />

            <Card sx={{ p: 2, mb: 3 }}>
                <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        label="Search invoice # / ref / recipient"
                        size="small"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        sx={{ minWidth: 280 }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        sx={{ minWidth: 240 }}
                    >
                        {STATUS_OPTIONS.map((o) => (
                            <MenuItem key={o.value || 'all'} value={o.value}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <YearGroupSelect
                        value={yearGroup}
                        onChange={setYearGroup}
                        anyLabel="All"
                        sx={{ minWidth: 160 }}
                    />
                    <TermSelect
                        value={term}
                        onChange={setTerm}
                        anyLabel="All"
                        sx={{ minWidth: 160 }}
                    />
                    <Button type="submit" variant="contained">Filter</Button>
                </Box>
            </Card>

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Invoice #</TableCell>
                                    <TableCell>Ref</TableCell>
                                    <TableCell>Recipient</TableCell>
                                    <TableCell>Term</TableCell>
                                    <TableCell>Total</TableCell>
                                    <TableCell>Due</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((i) => {
                                    const sub = studentSubline(i);
                                    return (
                                    <TableRow key={i._id}>
                                        <TableCell>{i.invoiceNumber}</TableCell>
                                        <TableCell>{i.paymentReference || '—'}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{recipientLabel(i)}</Typography>
                                            {sub && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {sub}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{i.term || '—'}</TableCell>
                                        <TableCell>{fmt(i.total, i.currency)}</TableCell>
                                        <TableCell>
                                            {i.dueDate ? new Date(i.dueDate).toLocaleDateString('en-GB') : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Chip
                                                    size="small"
                                                    label={i.status}
                                                    color={STATUS_COLORS[i.status] || 'default'}
                                                />
                                                {i.paymentMarkedByParent && i.status !== 'paid' && (
                                                    <Chip size="small" label="Marked paid by parent" color="warning" />
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            {['sent', 'overdue'].includes(i.status) && (
                                                <Tooltip
                                                    title={
                                                        i.lastReminderSentAt
                                                            ? `Last reminder: ${new Date(i.lastReminderSentAt).toLocaleString('en-GB')} (sent ${i.remindersSentCount || 1}×)`
                                                            : 'No reminders sent yet'
                                                    }
                                                >
                                                    <span>
                                                        <Button
                                                            onClick={() => sendReminder(i._id)}
                                                            size="small"
                                                            color={i.status === 'overdue' ? 'warning' : 'primary'}
                                                            disabled={reminderBusyId === i._id}
                                                        >
                                                            {reminderBusyId === i._id ? 'Sending…' : 'Send reminder'}
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            <Button
                                                size="small"
                                                onClick={async () => {
                                                    try {
                                                        const { data } = await axios.get(
                                                            `${API_URL}/api/admin/invoices/${i._id}/preview-pdf`,
                                                            { ...authHeaders(), responseType: 'blob' }
                                                        );
                                                        const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
                                                        window.open(url, '_blank', 'noopener');
                                                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                                                    } catch {}
                                                }}
                                                title="Open the PDF without sending"
                                            >
                                                PDF
                                            </Button>
                                            {i.status === 'draft' && (
                                                <Button
                                                    component={RouterLink}
                                                    to={`/invoices/new?editId=${i._id}`}
                                                    size="small"
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                            <Button
                                                component={RouterLink}
                                                to={`/invoices/new?duplicateOf=${i._id}`}
                                                size="small"
                                            >
                                                Duplicate
                                            </Button>
                                            <Button component={RouterLink} to={`/invoices/${i._id}`} size="small">
                                                Open
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    );
                                })}
                                {items.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8}>
                                            <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                                No invoices match the current filters.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </>
                )}
            </Card>
        </Container>
    );
}
